/**
 * Exports-menu logic (Wave 5, spec §2 item 4): which deliverable downloads
 * are available for the active MEP system, with discipline-appropriate
 * enable/disable and tooltip text. Pure — unit-tested without the DOM.
 *
 * Deliverables: BoQ CSV (all disciplines), riser SVG (fire-alarm only),
 * PDF report (all), DXF floor placements (all). IFC export of generated MEP
 * is explicitly out of v1 (spec §13 item 15).
 */
import { boqCsvUrl, placementsDxfUrl, reportPdfUrl, riserSvgUrl } from "../api/client";
import type { MepSystem } from "../api/types";

export type ExportKey = "boq_csv" | "riser_svg" | "report_pdf" | "dxf";

export interface ExportEntry {
  key: ExportKey;
  label: string;
  /** Download href; empty string when the entry is disabled. */
  href: string;
  /** Suggested file name for the `download` attribute. */
  filename: string;
  enabled: boolean;
  /** What the download contains (enabled) or why it is disabled. */
  tooltip: string;
}

const NO_SYSTEM = "Generate a system first — exports come from a generated variant.";

function disciplineLabel(discipline: string): string {
  return discipline === "hvac" ? "HVAC" : discipline === "fire_alarm" ? "fire-alarm" : discipline;
}

/**
 * Build the export entries for the active system. `system === null` (nothing
 * generated yet) renders every entry disabled with an explanatory tooltip —
 * the menu is always discoverable, never a dead-end.
 */
export function exportEntries(system: MepSystem | null, modelId: string): ExportEntry[] {
  const fire = system?.discipline === "fire_alarm";
  const label = system ? disciplineLabel(system.discipline) : "generated";

  return [
    {
      key: "boq_csv",
      label: "BoQ (CSV)",
      href: system ? boqCsvUrl(system.id) : "",
      filename: "boq.csv",
      enabled: system !== null,
      tooltip: system ? `Bill of quantities for the ${label} system` : NO_SYSTEM,
    },
    {
      key: "riser_svg",
      label: "Riser (SVG)",
      href: fire && system ? riserSvgUrl(system.id) : "",
      filename: "riser.svg",
      enabled: fire,
      tooltip: fire
        ? "Riser diagram: storeys, circuits, and devices per riser"
        : system
          ? "Riser diagrams apply to fire-alarm systems only"
          : NO_SYSTEM,
    },
    {
      key: "report_pdf",
      label: "Report (PDF)",
      href: system ? reportPdfUrl(system.id) : "",
      filename: "report.pdf",
      enabled: system !== null,
      tooltip: system
        ? "Rule-checked report: findings with citations, overrides, and data-confidence notes"
        : NO_SYSTEM,
    },
    {
      key: "dxf",
      label: "Placements (DXF)",
      href: system ? placementsDxfUrl(modelId) : "",
      filename: "placements.dxf",
      enabled: system !== null,
      tooltip: system
        ? `DXF export of the ${label} floor placements`
        : NO_SYSTEM,
    },
  ];
}
